import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CandidateDashboardLayout from '../components/layouts/CandidateDashboardLayout';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL } from '../config';

// Build headers with JWT Authorization from SessionManager
const buildAuthHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('candidate') || SessionManager.getSession('hr');
  }
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const CandidateReportsPage = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [overallStats, setOverallStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetails, setReportDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const session = SessionManager.getSession('candidate');
  const candidateId = session?.user_id;
  const candidateEmail = session?.email;

  useEffect(() => {
    if (!candidateId) {
      navigate('/login');
      return;
    }
    loadAllTrainerReports();

    const refreshInterval = setInterval(() => {
      loadAllTrainerReports();
    }, 15000);

    return () => clearInterval(refreshInterval);
  }, [candidateId, navigate]);

  const loadAllTrainerReports = async () => {
    try {
      setLoading(true);
      const baseUrl = API_BASE_URL;

      const params = new URLSearchParams();
      if (candidateId) params.set('candidate_id', candidateId);
      if (candidateEmail) params.set('candidate_email', candidateEmail);
      const queryString = params.toString();

      // Fetch from BOTH old text-based trainer AND live voice sessions
      const [aiTrainerResponse, liveInterviewResponse] = await Promise.all([
        candidateId
          ? fetch(`${baseUrl}/ai-trainer/candidate-sessions/${candidateId}`, { headers: buildAuthHeaders() }).then(r => r.json()).catch(() => ({ sessions: [] }))
          : Promise.resolve({ sessions: [] }),
        fetch(`${baseUrl}/live-interview/candidate-sessions${queryString ? `?${queryString}` : ''}`).then(r => r.json()).catch(() => ({ sessions: [] }))
      ]);

      // Filter live sessions to ONLY trainer type (not real job interviews)
      const liveTrainerSessions = (liveInterviewResponse.sessions || [])
        .filter(s => s.interview_type === 'trainer')
        .map(s => ({
          ...s,
          source: 'live_interview',
          session_type: 'live_voice'
        }));

      const oldTrainerSessions = (aiTrainerResponse.sessions || []).map(s => ({
        ...s,
        source: 'ai_trainer'
      }));

      // Merge and sort newest first
      const allSessions = [...oldTrainerSessions, ...liveTrainerSessions].sort((a, b) => {
        const dateA = new Date(a.completed_at || a.created_at || 0);
        const dateB = new Date(b.completed_at || b.created_at || 0);
        return dateB - dateA;
      });

      setReports(allSessions);

      // Calculate overall stats
      const completedSessions = allSessions.filter(s => s.status === 'completed');
      const scores = completedSessions
        .map(s => s.overall_score || s.score || 0)
        .filter(s => s > 0);
      const avgScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;

      setOverallStats({
        total_sessions: allSessions.length,
        completed_sessions: completedSessions.length,
        completion_rate: allSessions.length > 0 ? Math.round((completedSessions.length / allSessions.length) * 100) : 0,
        overall_average_score: avgScore
      });
    } catch (error) {
      console.error('Error loading trainer reports:', error);
      setReports([]);
      setOverallStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = async (report) => {
    const sessionId = report.session_id || report.id;
    if (selectedReport === sessionId) {
      setSelectedReport(null);
      setReportDetails(null);
      return;
    }

    try {
      setLoadingDetails(true);
      setSelectedReport(sessionId);
      const baseUrl = API_BASE_URL;

      const reportUrl = report.source === 'live_interview'
        ? `${baseUrl}/live-interview/session/${sessionId}/report`
        : `${baseUrl}/ai-trainer/session/${sessionId}/report`;

      const response = await fetch(reportUrl);
      if (!response.ok) throw new Error('Failed to load report');

      const data = await response.json();
      setReportDetails(data);
    } catch (error) {
      console.error('Error loading report details:', error);
      setReportDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-50 border-emerald-100';
    if (score >= 60) return 'bg-amber-50 border-amber-100';
    return 'bg-red-50 border-red-100';
  };

  const getGrade = (score) => {
    if (score >= 90) return 'A+';
    if (score >= 80) return 'A';
    if (score >= 70) return 'B';
    if (score >= 60) return 'C';
    if (score >= 50) return 'D';
    return 'F';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <CandidateDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-[#0a1929] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-500 text-sm">Loading reports...</p>
          </div>
        </div>
      </CandidateDashboardLayout>
    );
  }

  return (
    <CandidateDashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Training Reports</h1>
            <p className="text-gray-500 text-sm mt-1">Track your interview training progress and performance</p>
          </div>
          <button
            onClick={() => navigate('/ai-trainer')}
            className="px-4 py-2.5 bg-[#0a1929] hover:bg-[#0d2137] text-white text-sm font-semibold rounded-xl transition-colors flex items-center gap-2"
          >
            <Icon name="add" size={18} className="text-white" />
            New Training Session
          </button>
        </div>

        {/* Overall Stats */}
        {overallStats && reports.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Sessions', value: overallStats.total_sessions, icon: 'psychology' },
              { label: 'Completed', value: overallStats.completed_sessions, icon: 'check_circle' },
              { label: 'Completion Rate', value: `${overallStats.completion_rate}%`, icon: 'trending_up' },
              { label: 'Average Score', value: overallStats.overall_average_score ? `${overallStats.overall_average_score}%` : 'N/A', icon: 'grade' },
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                    <Icon name={stat.icon} size={14} className="text-[#0a1929]" />
                  </div>
                  <span className="text-xs text-gray-500">{stat.label}</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{stat.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Reports List */}
        {reports.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 text-center py-16">
            <div className="w-14 h-14 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Icon name="psychology" size={28} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Training Reports Yet</h3>
            <p className="text-gray-500 text-sm mb-6">Complete AI training sessions to see your detailed reports</p>
            <button
              onClick={() => navigate('/ai-trainer')}
              className="px-6 py-2.5 bg-[#0a1929] hover:bg-[#0d2137] text-white text-sm font-semibold rounded-xl transition-colors"
            >
              Start AI Training
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => {
              const sessionId = report.session_id || report.id;
              const score = report.overall_score || report.score || 0;
              const grade = report.overall_grade || getGrade(score);
              const title = report.job_title || report.position || 'AI Training';
              const date = report.completed_at || report.created_at;
              const conversationTurns = report.conversation_turns || report.total_turns || 0;
              const isLive = report.source === 'live_interview';
              const isExpanded = selectedReport === sessionId;

              return (
                <div key={sessionId} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4 flex-1">
                        {/* Score box */}
                        {score > 0 ? (
                          <div className={`w-14 h-14 rounded-xl border flex flex-col items-center justify-center flex-shrink-0 ${getScoreBg(score)}`}>
                            <span className={`text-lg font-bold ${getScoreColor(score)}`}>{Math.round(score)}%</span>
                          </div>
                        ) : (
                          <div className="w-14 h-14 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center flex-shrink-0">
                            <Icon name="hourglass_empty" size={20} className="text-gray-400" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              report.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                            }`}>
                              {report.status === 'completed' ? 'Completed' : 'In Progress'}
                            </span>
                          </div>

                          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Icon name={isLive ? 'record_voice_over' : 'chat'} size={12} />
                              {isLive ? 'Live Voice' : 'Text-based'}
                            </span>
                            {conversationTurns > 0 && (
                              <span className="flex items-center gap-1">
                                <Icon name="forum" size={12} />
                                {conversationTurns} conversation turns
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Icon name="schedule" size={12} />
                              {formatDate(date)}
                            </span>
                            {score > 0 && (
                              <span className="flex items-center gap-1">
                                <Icon name="grade" size={12} />
                                Grade: {grade}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => handleViewReport(report)}
                        className="px-4 py-2 bg-[#0a1929] hover:bg-[#0d2137] text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5 flex-shrink-0 ml-3"
                      >
                        <Icon name={isExpanded ? 'expand_less' : 'visibility'} size={14} className="text-white" />
                        {isExpanded ? 'Hide' : 'View Report'}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Report Details */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50 p-5">
                      {loadingDetails ? (
                        <div className="text-center py-6">
                          <div className="w-6 h-6 border-2 border-[#0a1929] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                          <p className="text-gray-500 text-xs">Loading report...</p>
                        </div>
                      ) : reportDetails ? (
                        <div>
                          {/* Score Summary */}
                          <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
                              <p className="text-2xl font-bold text-gray-900">{Math.round(reportDetails.overall_score || reportDetails.score || score)}%</p>
                              <p className="text-xs text-gray-500">Overall Score</p>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
                              <p className="text-2xl font-bold text-gray-900">{reportDetails.overall_grade || reportDetails.grade || grade}</p>
                              <p className="text-xs text-gray-500">Grade</p>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
                              <p className="text-2xl font-bold text-gray-900">{reportDetails.conversation_turns || reportDetails.answered_questions || conversationTurns}</p>
                              <p className="text-xs text-gray-500">Questions</p>
                            </div>
                          </div>

                          {/* Question-level details */}
                          {(reportDetails.questions || reportDetails.answers || reportDetails.question_evaluations || []).length > 0 && (
                            <div>
                              <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Question Analysis</h4>
                              <div className="space-y-2">
                                {(reportDetails.questions || reportDetails.answers || reportDetails.question_evaluations || []).map((q, idx) => (
                                  <div key={idx} className="bg-white rounded-lg p-3 border border-gray-200">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-sm font-medium text-gray-900">Q{q.question_number || idx + 1}</span>
                                      <div className="flex items-center gap-2">
                                        {q.grade && (
                                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                            q.grade === 'A+' || q.grade === 'A' ? 'bg-emerald-50 text-emerald-700' :
                                            q.grade === 'B+' || q.grade === 'B' ? 'bg-blue-50 text-blue-700' :
                                            q.grade === 'C+' || q.grade === 'C' ? 'bg-amber-50 text-amber-700' :
                                            'bg-red-50 text-red-700'
                                          }`}>
                                            {q.grade}
                                          </span>
                                        )}
                                        {(q.score !== undefined && q.score !== null) && (
                                          <span className="text-xs font-medium text-gray-600">{Math.round(q.score)}%</span>
                                        )}
                                      </div>
                                    </div>
                                    {q.question && (
                                      <p className="text-xs text-gray-600 mb-1">{q.question}</p>
                                    )}
                                    {q.feedback && (
                                      <p className="text-xs text-gray-500">{q.feedback}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm text-center py-4">Report details not available</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </CandidateDashboardLayout>
  );
};

export default CandidateReportsPage;
